
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function WhyMedilinePage() {
  const advantages = [
    {
      icon: 'ri-map-2-line',
      title: 'California-Focused Operations',
      description: 'Deep understanding of California healthcare regulations, licensing requirements, and market dynamics. Our statewide presence enables rapid response and deployment anywhere in California.',
      stats: ['Statewide Coverage', '48-Hour Response', 'Local Expertise']
    },
    {
      icon: 'ri-rocket-line',
      title: 'Rapid Deployment Capability',
      description: 'Industry-leading 48-72 hour emergency deployment capability with complete operational support. Our logistics team and equipment fleet are optimized for speed without compromising quality.',
      stats: ['48-72 Hour Deployment', 'Emergency Response', '24/7 Availability']
    },
    {
      icon: 'ri-team-line',
      title: 'Experienced Logistics Team',
      description: 'Specialized medical imaging logistics professionals with decades of combined experience. Our team understands the unique challenges of healthcare facility operations and patient care continuity.',
      stats: ['15+ Years Experience', 'Certified Professionals', 'Healthcare Specialists']
    },
    {
      icon: 'ri-file-list-line',
      title: 'Flexible Contract Terms',
      description: 'Customizable leasing arrangements designed to fit your operational and financial requirements. From short-term emergency deployments to multi-year agreements, we adapt to your needs.',
      stats: ['Short-Term Options', 'Long-Term Agreements', 'Custom Solutions']
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Healthcare Compliance-Driven',
      description: 'Full regulatory compliance, licensing, and quality assurance protocols built into every deployment. We maintain the highest standards for patient safety and operational excellence.',
      stats: ['Full Licensing', 'Quality Assurance', 'Safety Protocols']
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Dedicated Support Team',
      description: 'Round-the-clock technical support and customer service ensuring maximum uptime and operational efficiency. Your success is our priority throughout the entire lease term.',
      stats: ['24/7 Support', 'Preventative Maintenance', 'Emergency Response']
    }
  ];

  const differentiators = [
    {
      title: 'Single-Vendor Simplicity',
      description: 'One point of contact for all aspects of mobile imaging deployment, from equipment to staffing to maintenance.'
    },
    {
      title: 'State-of-the-Art Equipment',
      description: 'Modern, well-maintained imaging systems with the latest technology and full PACS integration capabilities.'
    },
    {
      title: 'Transparent Pricing',
      description: 'Clear, straightforward pricing with no hidden fees or unexpected costs throughout the lease term.'
    },
    {
      title: 'Proven Track Record',
      description: 'Over 15 years serving California healthcare facilities with 98% client satisfaction and retention rates.'
    },
    {
      title: 'Operational Excellence',
      description: 'Systematic approach to deployment, maintenance, and support ensuring consistent, reliable performance.'
    },
    {
      title: 'Partnership Approach',
      description: 'We view our clients as long-term partners and invest in understanding your unique operational challenges.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <main className="pt-24 sm:pt-32 pb-12 sm:pb-20">
        {/* Hero Section */}
        <section className="px-4 sm:px-6 mb-12 sm:mb-20">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-slate-blue text-[13px] uppercase tracking-[2px] font-medium mb-3 sm:mb-4">
              WHY MEDILINE
            </p>
            <h1 className="text-navy text-[28px] sm:text-[40px] lg:text-[48px] font-semibold leading-tight mb-4 sm:mb-6">
              Why Healthcare Organizations Choose Mediline Services
            </h1>
            <p className="text-[#5A6C7D] text-base sm:text-lg leading-relaxed">
              For over 15 years, Mediline Services has been California&apos;s trusted partner for mobile imaging solutions. Our commitment to operational excellence, regulatory compliance, and customer success sets us apart in the healthcare imaging industry.
            </p>
          </div>
        </section>

        {/* Core Advantages */}
        <section className="px-4 sm:px-6 mb-16 sm:mb-32">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-navy text-[24px] sm:text-[30px] lg:text-[36px] font-semibold text-center mb-10 sm:mb-16">
              Our Core Advantages
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
              {advantages.map((advantage, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 sm:p-8 shadow-[0_4px_24px_rgba(15,30,60,0.08)] hover:shadow-[0_8px_32px_rgba(15,30,60,0.12)] transition-all duration-300"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-slate-blue rounded-full flex items-center justify-center mb-4 sm:mb-6">
                    <i className={`${advantage.icon} text-white text-xl sm:text-2xl`}></i>
                  </div>
                  <h3 className="text-navy text-lg sm:text-xl font-semibold mb-2 sm:mb-3">
                    {advantage.title}
                  </h3>
                  <p className="text-[#5A6C7D] text-sm sm:text-[15px] leading-relaxed mb-4 sm:mb-6">
                    {advantage.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {advantage.stats.map((stat, idx) => (
                      <span
                        key={idx}
                        className="bg-slate-blue/10 text-slate-blue px-3 py-1 rounded-full text-xs font-medium"
                      >
                        {stat}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Differentiators */}
        <section className="px-4 sm:px-6 py-12 sm:py-20 bg-[#F7F9FB]">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-navy text-[24px] sm:text-[30px] lg:text-[36px] font-semibold text-center mb-10 sm:mb-16">
              What Sets Us Apart
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {differentiators.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 sm:p-8 shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-slate-blue rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-base sm:text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-navy text-base sm:text-lg font-semibold mb-1 sm:mb-2">
                        {item.title}
                      </h3>
                      <p className="text-[#5A6C7D] text-sm sm:text-[15px] leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonial Highlight */}
        <section className="px-4 sm:px-6 py-12 sm:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-navy to-navy-light rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center">
              <div className="text-white/30 text-[50px] sm:text-[80px] font-serif leading-none mb-4 sm:mb-6">&ldquo;</div>
              <blockquote className="text-white text-lg sm:text-xl lg:text-2xl font-serif leading-relaxed mb-6 sm:mb-8">
                Mediline&apos;s professionalism and attention to detail made our MRI deployment seamless. Their team anticipated every need and delivered exactly what they promised. We&apos;ve worked with them for three years now and wouldn&apos;t consider anyone else.
              </blockquote>
              <div className="text-white text-base sm:text-lg font-semibold mb-1">
                Michael Rodriguez, COO
              </div>
              <div className="text-white/70 text-sm sm:text-base">
                Central Valley Health System
              </div>
            </div>
          </div>
        </section>

        {/* Certifications & Compliance */}
        <section className="px-4 sm:px-6 py-12 sm:py-20 bg-[#F7F9FB]">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-navy text-[24px] sm:text-[30px] lg:text-[36px] font-semibold mb-4 sm:mb-6">
              Certifications & Compliance
            </h2>
            <p className="text-[#5A6C7D] text-base sm:text-lg leading-relaxed mb-8 sm:mb-12 max-w-3xl mx-auto">
              Mediline Services maintains all required licenses, certifications, and insurance coverage to operate mobile imaging equipment throughout California.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
              <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md">
                <i className="ri-shield-check-line text-slate-blue text-3xl sm:text-4xl mb-2 sm:mb-3"></i>
                <div className="text-navy font-semibold text-xs sm:text-sm">California Licensed</div>
              </div>
              <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md">
                <i className="ri-file-shield-line text-slate-blue text-3xl sm:text-4xl mb-2 sm:mb-3"></i>
                <div className="text-navy font-semibold text-xs sm:text-sm">Fully Insured</div>
              </div>
              <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md">
                <i className="ri-award-line text-slate-blue text-3xl sm:text-4xl mb-2 sm:mb-3"></i>
                <div className="text-navy font-semibold text-xs sm:text-sm">ACR Accredited</div>
              </div>
              <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md">
                <i className="ri-verified-badge-line text-slate-blue text-3xl sm:text-4xl mb-2 sm:mb-3"></i>
                <div className="text-navy font-semibold text-xs sm:text-sm">HIPAA Compliant</div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 sm:px-6 mt-12 sm:mt-20">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-navy to-navy-light rounded-2xl sm:rounded-3xl p-8 sm:p-12 lg:p-16 text-center">
            <h2 className="text-white text-[24px] sm:text-[30px] lg:text-[36px] font-semibold mb-3 sm:mb-4">
              Experience the Mediline Difference
            </h2>
            <p className="text-white/85 text-base sm:text-lg mb-6 sm:mb-8">
              Join hundreds of California healthcare facilities who trust Mediline Services for their mobile imaging needs.
            </p>
            <a
              href="/contact"
              className="inline-block bg-white text-navy px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base font-semibold hover:shadow-2xl transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Get Started Today
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
